import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    // Detect browser language
    const browserLang = navigator.language || navigator.userLanguage;
    const lang = browserLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
    setLanguage(lang);

    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a small delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const translations = {
    en: {
      title: '🍪 We use cookies',
      text: 'This site uses cookies to improve your experience and analyze our traffic. By clicking "Accept", you consent to the use of analytics cookies.',
      learnMore: 'Learn more',
      reject: 'Reject',
      accept: 'Accept',
      close: 'Close'
    },
    fr: {
      title: '🍪 Nous utilisons des cookies',
      text: 'Ce site utilise des cookies pour améliorer votre expérience et analyser notre trafic. En cliquant sur "Accepter", vous consentez à l\'utilisation de cookies d\'analyse.',
      learnMore: 'En savoir plus',
      reject: 'Refuser',
      accept: 'Accepter',
      close: 'Fermer'
    }
  };

  const t = translations[language];

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setShowBanner(false);
    
    // Enable PostHog analytics
    if (window.posthog) {
      window.posthog.opt_in_capturing();
    }
    
    // Enable Google Analytics - Send initial page view
    if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
      // Send page view event
      window.gtag('event', 'page_view', {
        page_path: window.location.pathname
      });
    }

    // Enable Meta Pixel - Send initial PageView
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', 'PageView');
    }
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    setShowBanner(false);
    
    // Disable PostHog analytics
    if (window.posthog) {
      window.posthog.opt_out_capturing();
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-slate-900/95 backdrop-blur-sm border-t border-orange-500/20 shadow-2xl">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          
          {/* Icon & Text */}
          <div className="flex items-start gap-3 flex-1">
            <Cookie className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />
            <div className="text-sm text-gray-300">
              <p className="font-semibold text-white mb-1">{t.title}</p>
              <p>
                {t.text}{' '}
                <a 
                  href="/privacy-policy" 
                  className="text-orange-400 hover:text-orange-300 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.learnMore} about our privacy policy
                </a>
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={handleRejectAll}
              className="flex-1 md:flex-none px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-lg transition-colors"
            >
              {t.reject}
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 md:flex-none px-6 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors shadow-lg"
            >
              {t.accept}
            </button>
            <button
              onClick={handleRejectAll}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              aria-label={t.close}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
